const REVIEWS_HASH = '#reviews';
const TRIGGER_SELECTOR = '[data-pdp-reviews-trigger]';
const REVIEWS_ACCORDION_SELECTOR = '#reviews, accordion-custom[data-reviews-accordion]';
const YOTPO_WIDGET_SELECTOR = '.yotpo-widget-instance';
const YOTPO_RATING_FALLBACK_SELECTOR = '[data-pdp-yotpo-rating-fallback]';
const YOTPO_SUMMARY_SELECTOR = '.yotpo-bottom-line[average-score]';
const YOTPO_REVIEWS_MAIN_WIDGET_SELECTOR = '#yotpo-reviews-main-widget';
const YOTPO_REVIEWS_LAYOUT_SELECTOR = '.yotpo-base-layout';
const REVIEWS_EMPTY_STATE_SELECTOR = '[data-pdp-yotpo-empty-state]';
const DEFAULT_REVIEWS_EMPTY_STATE_TITLE = 'No reviews yet';
const DEFAULT_REVIEWS_EMPTY_STATE_BODY = 'Be the first to share your experience with this product.';

/** @type {WeakMap<HTMLElement, AbortController>} */
const triggerBindingControllers = new WeakMap();

/** @type {WeakMap<HTMLDetailsElement, AbortController>} */
const accordionToggleBindingControllers = new WeakMap();

/** @type {MutationObserver | null} */
let yotpoRatingObserver = null;

/** @type {number | null} */
let yotpoRatingHydrationFrame = null;

/** @type {MutationObserver | null} */
let yotpoReviewsEmptyStateObserver = null;

/** @type {number | null} */
let yotpoReviewsEmptyStateFrame = null;

function stopYotpoReviewsEmptyStateObserver() {
  yotpoReviewsEmptyStateObserver?.disconnect();
  yotpoReviewsEmptyStateObserver = null;

  if (yotpoReviewsEmptyStateFrame !== null) {
    cancelAnimationFrame(yotpoReviewsEmptyStateFrame);
    yotpoReviewsEmptyStateFrame = null;
  }
}

function isYotpoReviewsEmptyStateFallbackEnabled() {
  const script = getPdpReviewsScript();
  return script?.dataset.pdpYotpoEmptyStateFallback !== 'false';
}

function getPdpReviewsScript() {
  return document.querySelector('script[data-pdp-yotpo-empty-state-fallback]');
}

function getYotpoReviewsEmptyStateCopy() {
  const script = getPdpReviewsScript();
  return {
    title: script?.dataset.pdpYotpoEmptyStateTitle || DEFAULT_REVIEWS_EMPTY_STATE_TITLE,
    body: script?.dataset.pdpYotpoEmptyStateBody || DEFAULT_REVIEWS_EMPTY_STATE_BODY,
  };
}

/**
 * Yotpo occasionally mounts an empty Reviews 3.0 layout instead of the
 * configurable empty state. Keep the accordion useful without replacing
 * Yotpo's normal review or empty-state experiences.
 */
function syncYotpoReviewsEmptyState() {
  const accordion = getReviewsAccordion();
  const widgetInstance = accordion?.querySelector(YOTPO_WIDGET_SELECTOR);
  if (!(widgetInstance instanceof HTMLElement)) return;

  const widget = widgetInstance.querySelector(YOTPO_REVIEWS_MAIN_WIDGET_SELECTOR);
  const layout = widget?.querySelector(YOTPO_REVIEWS_LAYOUT_SELECTOR);
  const fallback = widgetInstance.parentElement?.querySelector(REVIEWS_EMPTY_STATE_SELECTOR);

  if (!isYotpoReviewsEmptyStateFallbackEnabled()) {
    widget?.removeAttribute('hidden');
    fallback?.remove();
    return;
  }

  if (!(widget instanceof HTMLElement) || !(layout instanceof HTMLElement)) {
    fallback?.remove();
    return;
  }

  // A populated widget, or Yotpo's own empty state, contains rendered elements.
  const isEmptyLayout = layout.childElementCount === 0;
  if (!isEmptyLayout) {
    widget.hidden = false;
    fallback?.remove();
    return;
  }

  widget.hidden = true;
  if (fallback instanceof HTMLElement) return;

  const emptyState = document.createElement('div');
  const copy = getYotpoReviewsEmptyStateCopy();
  emptyState.className = 'pdp-reviews-empty-state';
  emptyState.dataset.pdpYotpoEmptyState = 'true';
  emptyState.setAttribute('role', 'status');

  const title = document.createElement('p');
  title.className = 'pdp-reviews-empty-state__title';
  title.textContent = copy.title;

  const body = document.createElement('p');
  body.className = 'pdp-reviews-empty-state__body';
  body.textContent = copy.body;

  emptyState.append(title, body);
  widgetInstance.after(emptyState);
}

function queueYotpoReviewsEmptyStateSync() {
  if (yotpoReviewsEmptyStateFrame !== null) return;

  yotpoReviewsEmptyStateFrame = requestAnimationFrame(() => {
    yotpoReviewsEmptyStateFrame = null;
    syncYotpoReviewsEmptyState();
  });
}

function observeYotpoReviewsEmptyState() {
  stopYotpoReviewsEmptyStateObserver();

  if (!isYotpoReviewsEmptyStateFallbackEnabled()) {
    syncYotpoReviewsEmptyState();
    return;
  }

  const accordion = getReviewsAccordion();
  if (!(accordion instanceof HTMLElement)) return;

  yotpoReviewsEmptyStateObserver = new MutationObserver(queueYotpoReviewsEmptyStateSync);
  yotpoReviewsEmptyStateObserver.observe(accordion, { childList: true, subtree: true });
  queueYotpoReviewsEmptyStateSync();
}

function getYotpoRatingSummary() {
  const accordion = getReviewsAccordion();
  const summary = accordion?.querySelector(YOTPO_SUMMARY_SELECTOR);
  if (!(summary instanceof HTMLElement)) return null;

  const rating = Number.parseFloat(summary.getAttribute('average-score') || '');
  const countText = summary.querySelector('.yotpo-bottom-line-text')?.textContent || summary.textContent || '';
  const countMatch = countText.match(/([\d,]+)\s+reviews?/i);
  const count = Number.parseInt(countMatch?.[1]?.replaceAll(',', '') || '', 10);

  if (!Number.isFinite(rating) || rating <= 0 || !Number.isFinite(count) || count <= 0) return null;

  return { rating, count };
}

/**
 * Populate the theme's rating block from Yotpo when Shopify's standard review
 * metafields have not been synced for the current product.
 *
 * @param {ParentNode} scope
 * @returns {boolean} Whether every fallback on the page has been hydrated.
 */
function hydrateYotpoRatingFallbacks(scope = document) {
  const fallbacks = Array.from(scope.querySelectorAll(YOTPO_RATING_FALLBACK_SELECTOR)).filter(
    (fallback) => fallback instanceof HTMLElement && fallback.dataset.pdpYotpoRatingHydrated !== 'true'
  );

  if (fallbacks.length === 0) return true;

  const summary = getYotpoRatingSummary();
  if (!summary) return false;

  fallbacks.forEach((fallback) => {
    if (!(fallback instanceof HTMLElement)) return;

    const stars = Array.from(fallback.querySelectorAll('[data-pdp-rating-star]'));
    const halfFill = fallback.dataset.pdpRatingHalfFill || '';

    stars.forEach((star, index) => {
      const fill = Math.max(0, Math.min(1, summary.rating - index));
      const isFilled = fill >= 0.3;
      const isHalf = fill >= 0.3 && fill <= 0.7;
      const starUse = star.querySelector('use');

      star.classList.toggle('filled-star', isFilled);
      if (starUse) {
        if (isHalf && halfFill) {
          starUse.setAttribute('fill', halfFill);
        } else {
          starUse.removeAttribute('fill');
        }
      }
    });

    const ratingElement = fallback.querySelector('.rating');
    if (ratingElement instanceof HTMLElement) {
      ratingElement.setAttribute('aria-label', `Rating of this product is ${summary.rating.toFixed(1)} out of 5`);
    }

    const ratingValue = fallback.querySelector('[data-pdp-yotpo-rating-value]');
    if (ratingValue) ratingValue.textContent = summary.rating.toFixed(1);

    const ratingCount = fallback.querySelector('[data-pdp-yotpo-rating-count]');
    if (ratingCount) ratingCount.textContent = summary.count.toLocaleString();

    fallback.dataset.pdpYotpoRatingHydrated = 'true';
    fallback.hidden = false;
  });

  bindTriggers(scope);
  return true;
}

function stopYotpoRatingObserver() {
  yotpoRatingObserver?.disconnect();
  yotpoRatingObserver = null;

  if (yotpoRatingHydrationFrame !== null) {
    cancelAnimationFrame(yotpoRatingHydrationFrame);
    yotpoRatingHydrationFrame = null;
  }
}

function observeYotpoRatingFallbacks() {
  stopYotpoRatingObserver();

  if (hydrateYotpoRatingFallbacks()) return;

  const main = document.querySelector('main') || document.documentElement;
  yotpoRatingObserver = new MutationObserver(() => {
    if (yotpoRatingHydrationFrame !== null) return;

    yotpoRatingHydrationFrame = requestAnimationFrame(() => {
      yotpoRatingHydrationFrame = null;
      if (hydrateYotpoRatingFallbacks()) stopYotpoRatingObserver();
    });
  });
  yotpoRatingObserver.observe(main, { childList: true, subtree: true });
}

/**
 * @returns {HTMLElement | null}
 */
function getReviewsAccordion() {
  return document.querySelector(REVIEWS_ACCORDION_SELECTOR);
}

/**
 * @returns {HTMLDetailsElement | null}
 */
function getReviewsDetails() {
  const accordion = getReviewsAccordion();
  const details = accordion?.querySelector('details');
  return details instanceof HTMLDetailsElement ? details : null;
}

/**
 * @returns {boolean}
 */
function isReviewsAccordionOpen() {
  return getReviewsDetails()?.open === true;
}

/**
 * @returns {boolean}
 */
function isYotpoReady() {
  return typeof window.yotpoWidgetsContainer?.initWidgets === 'function';
}

/**
 * @param {HTMLElement} trigger
 */
function unbindTrigger(trigger) {
  triggerBindingControllers.get(trigger)?.abort();
  triggerBindingControllers.delete(trigger);
  delete trigger.dataset.pdpReviewsBound;
}

/**
 * @param {HTMLDetailsElement} details
 * @param {HTMLElement} accordion
 */
function unbindYotpoAccordionToggle(details, accordion) {
  accordionToggleBindingControllers.get(details)?.abort();
  accordionToggleBindingControllers.delete(details);
  delete accordion.dataset.pdpReviewsAccordionBound;
  delete accordion.dataset.pdpYotpoAccordionBound;
}

/**
 * @param {ParentNode} scope
 */
function resetBindings(scope = document) {
  scope.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
    if (trigger instanceof HTMLElement) unbindTrigger(trigger);
  });

  scope.querySelectorAll('accordion-custom').forEach((accordion) => {
    if (!(accordion instanceof HTMLElement)) return;

    const details = accordion.querySelector('details');
    if (details instanceof HTMLDetailsElement) {
      unbindYotpoAccordionToggle(details, accordion);
      return;
    }

    delete accordion.dataset.pdpReviewsAccordionBound;
    delete accordion.dataset.pdpYotpoAccordionBound;
  });
}

/**
 * Clear stale Yotpo markup so initWidgets can mount the new product's widgets after AJAX morphs.
 * @param {ParentNode} scope
 */
function resetYotpoWidgetInstances(scope = document) {
  scope.querySelectorAll(YOTPO_WIDGET_SELECTOR).forEach((instance) => {
    if (!(instance instanceof HTMLElement)) return;

    const hasRenderedYotpo =
      instance.querySelector('.yotpo, [class*="yotpo-"]') != null || instance.dataset.yotpoInitialized === 'true';

    if (!hasRenderedYotpo) return;

    instance.replaceChildren();
    delete instance.dataset.yotpoInitialized;
  });

  scope.querySelectorAll(REVIEWS_EMPTY_STATE_SELECTOR).forEach((emptyState) => emptyState.remove());
}

/**
 * @param {ParentNode} scope
 */
function initYotpoWidgets(scope = document) {
  if (!isYotpoReady()) return;

  resetYotpoWidgetInstances(scope);

  requestAnimationFrame(() => {
    try {
      window.yotpoWidgetsContainer.initWidgets();
      queueYotpoReviewsEmptyStateSync();
    } catch (error) {
      console.warn('Yotpo initWidgets failed on PDP:', error);
    }
  });
}

/**
 * Ensure the open reviews panel is visible before Yotpo measures layout.
 */
function prepareReviewsPanelForRender() {
  const details = getReviewsDetails();
  if (!(details instanceof HTMLDetailsElement) || !details.open) return;

  const content = details.querySelector('.details-content');
  if (!(content instanceof HTMLElement)) return;

  content.style.display = 'block';
  content.style.blockSize = 'auto';
  content.style.height = 'auto';
  content.style.opacity = '1';
  content.style.overflow = 'visible';
  content.style.visibility = 'visible';
}

/**
 * Re-init Yotpo widgets after the reviews accordion opens.
 */
function refreshYotpoReviewsWidget() {
  if (!isReviewsAccordionOpen() || !isYotpoReady()) return;

  prepareReviewsPanelForRender();
  initYotpoWidgets(getReviewsAccordion() || document);
}

/**
 * @param {HTMLElement} accordionEl
 */
function openReviewsAccordion(accordionEl) {
  const accordionCustom =
    accordionEl.tagName === 'ACCORDION-CUSTOM' ? accordionEl : accordionEl.closest('accordion-custom');

  if (accordionCustom instanceof HTMLElement && 'details' in accordionCustom) {
    /** @type {{ details: HTMLDetailsElement }} */
    (accordionCustom).details.open = true;
  } else {
    const details = accordionEl.querySelector('details');
    if (details instanceof HTMLDetailsElement) details.open = true;
  }

  // Yotpo init runs from the details toggle listener when opening; avoid double initWidgets().
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  accordionEl.scrollIntoView({ behavior, block: 'start' });
}

function handleReviewsTrigger() {
  const accordion = getReviewsAccordion();
  if (accordion instanceof HTMLElement) openReviewsAccordion(accordion);
}

/**
 * @param {ParentNode} scope
 */
function bindTriggers(scope = document) {
  scope.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
    if (!(trigger instanceof HTMLElement) || trigger.dataset.pdpReviewsBound === 'true') return;

    unbindTrigger(trigger);

    const controller = new AbortController();
    triggerBindingControllers.set(trigger, controller);
    trigger.dataset.pdpReviewsBound = 'true';

    const { signal } = controller;

    trigger.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        handleReviewsTrigger();
      },
      { signal }
    );

    trigger.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleReviewsTrigger();
      },
      { signal }
    );
  });
}

/**
 * Bind toggle handlers for reviews and Q&A accordions that contain Yotpo widgets.
 * @param {ParentNode} scope
 */
function bindYotpoAccordionToggles(scope = document) {
  const accordions =
    scope instanceof HTMLElement && scope.matches('accordion-custom')
      ? [scope]
      : Array.from(scope.querySelectorAll('accordion-custom'));

  accordions.forEach((accordion) => {
    if (!(accordion instanceof HTMLElement) || accordion.dataset.pdpYotpoAccordionBound === 'true') return;

    const details = accordion.querySelector('details');
    const hasYotpo = accordion.querySelector(YOTPO_WIDGET_SELECTOR);

    if (!(details instanceof HTMLDetailsElement) || !hasYotpo) return;

    unbindYotpoAccordionToggle(details, accordion);

    const controller = new AbortController();
    accordionToggleBindingControllers.set(details, controller);
    accordion.dataset.pdpYotpoAccordionBound = 'true';

    if (accordion.matches(REVIEWS_ACCORDION_SELECTOR)) {
      accordion.dataset.pdpReviewsAccordionBound = 'true';
    }

    details.addEventListener(
      'toggle',
      () => {
        if (!details.open) return;

        if (accordion.matches(REVIEWS_ACCORDION_SELECTOR)) {
          refreshYotpoReviewsWidget();
          return;
        }

        initYotpoWidgets(accordion);
      },
      { signal: controller.signal }
    );
  });
}

function handleHash() {
  if (window.location.hash !== REVIEWS_HASH) return;

  const accordion = getReviewsAccordion();
  if (!(accordion instanceof HTMLElement)) return;

  requestAnimationFrame(() => openReviewsAccordion(accordion));
}

/**
 * Rebind PDP review UI and refresh Yotpo after stitched / combined-listing product morphs.
 */
function handleProductContentUpdated() {
  const scope = document.querySelector('main') || document;

  resetBindings(scope);
  bindTriggers(scope);
  bindYotpoAccordionToggles(scope);
  observeYotpoReviewsEmptyState();
  observeYotpoRatingFallbacks();
  handleHash();
  initYotpoWidgets(scope);
}

/**
 * @param {ParentNode} scope
 */
function init(scope = document) {
  bindTriggers(scope);
  bindYotpoAccordionToggles(scope);
  observeYotpoReviewsEmptyState();
  observeYotpoRatingFallbacks();
  handleHash();

  if (isReviewsAccordionOpen()) {
    refreshYotpoReviewsWidget();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});

document.addEventListener('mly:main-content-updated', handleProductContentUpdated);

document.addEventListener('product-information:accordion-layout-synced', () => {
  const scope = document.querySelector('.product-information--live-accordion') || document;
  initYotpoWidgets(scope);
  observeYotpoReviewsEmptyState();
  observeYotpoRatingFallbacks();

  if (isReviewsAccordionOpen()) {
    refreshYotpoReviewsWidget();
  }
});

window.addEventListener('hashchange', handleHash);
